import * as THREE from 'three'
import * as TWEEN from '@tweenjs/tween.js'
import { tweenGroup } from '@js/lib/tween'

import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'

import { randomArrayElement } from '@js/utils'
import { getActions } from '../utils'



export interface AnimatorOptions {
  camera?: THREE.Camera
  renderer?: THREE.WebGLRenderer | EffectComposer
  center?: THREE.Vector3
  centerPoint?: THREE.Vector3
  easing?: (k: number) => number
  followMouse?: boolean
}

export interface AnimatedObject extends THREE.Object3D {
  mixer?: THREE.AnimationMixer
  actions?: Record<string, THREE.AnimationAction>
}

export class BaseAnimator<T extends AnimatedObject> {
  protected object: T
  protected gltf: GLTF
  protected camera?: THREE.Camera
  protected centerPoint: THREE.Vector3
  protected duration = 0
  protected repeats = 1
  protected easing: (k: number) => number
  protected actionPlaying: THREE.AnimationAction | null = null
  private currentTween: TWEEN.Tween<{ progress: number }> | null = null

  constructor(object: T, gltf: GLTF, options: AnimatorOptions = {}) {
    this.object = object
    this.gltf = gltf
    this.camera = options.camera
    this.centerPoint = options.center ?? new THREE.Vector3()
    this.easing = options.easing ?? TWEEN.Easing.Linear.None
  }

  /* --------------------------------------------------------------------- */
  /* SETUP                                                                */
  /* --------------------------------------------------------------------- */

  setup(actionLabels: string[]): void {
    const actions = getActions(this.gltf, this.object, actionLabels)
    if (!actions || !this.object.actions) return

    this.play(actions[0])

    this.object.mixer?.addEventListener('finished', () => {
      const available = actions.filter(a => a !== this.actionPlaying)
      const next = randomArrayElement(available)
      this.play(next)
    })
  }


  /* --------------------------------------------------------------------- */
  /* PLAY                                                                 */
  /* --------------------------------------------------------------------- */

  play(action: THREE.AnimationAction): void {
    const clip = action.getClip()
    this.duration = clip.duration * this.repeats  // ✅ account for repeats!

    action.setLoop(THREE.LoopRepeat, this.repeats)
    action.clampWhenFinished = true

    this.actionPlaying = action
    action.reset().play()

    this.startTween()
  }

  /* --------------------------------------------------------------------- */
  /* TWEEN                                                                */
  /* --------------------------------------------------------------------- */

  /**
   * Override those functions to tweek the startTween function
   */
  protected onStart(): void {}
  
  protected onUpdate(_delta: number): void {}
  
  protected onComplete(): void {}

  protected onStop(): void {}


  /**
   * tweening function (don't touch)
   */
  protected startTween(): void {
    this.currentTween?.stop()

    const durationMs = this.duration * 1000
    let lastProgress = 0

    this.currentTween = new TWEEN.Tween({ progress: 0 }, tweenGroup)
      .to({ progress: 1 }, durationMs)
      .easing(this.easing)
      .onStart(() => this.onStart())
      .onUpdate(({ progress }) => {
        const delta = progress - lastProgress
        lastProgress = progress
        this.onUpdate(delta)
      })
      .onComplete(() => this.onComplete())
      .start()
  }


  /**
   * Update tween (don't touch)
   */
  update(delta: number): void {
    this.object.mixer?.update(delta)
  }
}

